import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import {
  API,
  IPayment,
  IEditPayment,
  TransactionContract,
  isTerminalTransaction,
  ICreateTransaction,
} from '@getgreenline/homi-shared'
import { FormError } from '../../components/FormError'
import { ChangePaymentTypeDropdown } from './ChangePaymentTypeDropdown'
import './ChangePaymentTypeModal.scss'
import { Track } from '../../Track'
import moment from 'moment'
import { BlazeButton } from '../CustomButtons/BlazeButton'
import { Switch } from 'antd'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentEmployeeId: string
  payment: IPayment
  onUpdate: () => void
  onClose: () => void
}

interface State {
  isSaving: boolean
  errorMessage?: string
  floatAdjustment: boolean
  notes: string
  paymentTransactions: TransactionContract[]
  editPaymentPennyAdjustment: number
}

@inject('currentCompanyStore')
@observer
export class ChangePaymentTypeModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      paymentTransactions: [],
      isSaving: false,
      errorMessage: undefined,
      floatAdjustment: true,
      notes: '',
      editPaymentPennyAdjustment: 0,
    }
  }

  componentDidMount() {
    const editPaymentPennyAdjustment = this.props.payment.pennyAdjustment
    const transactions: TransactionContract[] = this.props.payment.transactions.map(
      (transaction) => new TransactionContract(transaction),
    )

    this.setState({
      paymentTransactions: transactions,
      editPaymentPennyAdjustment,
    })
  }

  get checkMissingInvoiceIds() {
    const { paymentTransactions } = this.state

    return (
      paymentTransactions.filter((trans) => {
        return isTerminalTransaction(trans.paymentTypeId) && trans.terminalInvoiceId === null
      }).length > 0
    )
  }

  get metaData() {
    const { floatAdjustment, notes } = this.state

    return {
      employeeId: this.props.currentEmployeeId,
      floatAdjustment,
      notes,
    }
  }

  changePennyAdjustment = (amount: number) => {
    this.setState({ editPaymentPennyAdjustment: amount })
  }

  submit = async () => {
    if (this.checkMissingInvoiceIds) {
      return
    }
    const { editPaymentPennyAdjustment } = this.state

    const company = this.props.currentCompanyStore!.company!
    const payment = this.props.payment
    this.setState({ isSaving: true })
    await Promise.all(
      this.state.paymentTransactions.map((transaction) =>
        API.updateTransactionById(company.id, transaction.id, transaction),
      ),
    )
    const existingTransaction: ICreateTransaction[] = []
    for (const transaction of payment.transactions) {
      const trans = this.state.paymentTransactions.find((transac) => transac.id === transaction.id)
      const currentTransaction: ICreateTransaction = {
        amount: trans ? trans.amount : transaction.amount,
        status: transaction.status,
        completeDate: transaction.completeDate ? moment(transaction.completeDate).toDate() : null,
        paymentTypeId: transaction.paymentTypeId,
        terminalInvoiceId: transaction.terminalInvoiceId,
        metadata: transaction.metadata,
      }
      existingTransaction.push(currentTransaction)
    }

    const oldPayment: IEditPayment = {
      pennyAdjustment: editPaymentPennyAdjustment,
      transactions: existingTransaction,
    }
    await API.updatePayment(company.id, payment.id, oldPayment, this.metaData)
      .then(() => {
        Track.track('dashboard:payments_paymentTypeUpdated', payment)
        this.props.onUpdate()
      })
      .catch((error) => {
        this.setState({ isSaving: false })
        this.setState({
          errorMessage: error.response.data.message || 'There was an error cancelling this sale',
        })
      })
  }

  render() {
    const payment = this.props.payment
    const { paymentTransactions, floatAdjustment, notes, editPaymentPennyAdjustment } = this.state

    const body = (
      <div>
        {
          // There are no floats to adjust if the payment is not from a POS device
          payment.deviceId && (
            <>
              <div className='mb-1'>
                <label className='mr-2'>Correct shift float</label>
                <span
                  className='fa fa-info-circle text-muted mr-4'
                  data-tip
                  data-for='correct-float'
                />
                <Tooltip id='correct-float' place='right' effect='solid'>
                  <div className='mb-1'>
                    The float for this sale's shift will be adjusted according to the change.
                  </div>
                  <div>Ex. $10 sale accidentally entered as credit.</div>
                  <div>Changing the sale to be cash will add $10 to the sale's shift float</div>
                  <div>
                    Note: If this sale is attached to an older shift, it will affect that old
                    shift's float.
                  </div>
                </Tooltip>
                <Switch
                  checked={floatAdjustment}
                  onChange={() =>
                    this.setState({
                      floatAdjustment: !floatAdjustment,
                    })
                  }
                />
              </div>
              {floatAdjustment && (
                <div className='form-group'>
                  <label className='mr-2'>Float adjustment notes</label>
                  <span className='fa fa-info-circle text-muted mr-2' data-tip data-for='notes' />
                  <Tooltip id='notes' place='right' effect='solid'>
                    <div>
                      If this edit affects the shift float, it will show up on the end-of-shift
                      receipt.
                    </div>
                  </Tooltip>
                  <textarea
                    className='form-control'
                    rows={2}
                    value={notes}
                    onChange={(e) =>
                      this.setState({
                        notes: e.target.value,
                      })
                    }
                  />
                </div>
              )}
            </>
          )
        }
        <br />
        <div className='mb-3'>
          If you made a mistake entering your payment type for this sale, you can edit them here.
        </div>
        {paymentTransactions.map((trans, index) => (
          <div key={index} className='payment-dropdown d-flex row mb-3 border'>
            <ChangePaymentTypeDropdown
              transaction={trans}
              payment={payment}
              changePennyAdjustment={this.changePennyAdjustment}
              editPaymentPennyAdjustment={editPaymentPennyAdjustment}
            />
          </div>
        ))}
      </div>
    )

    return (
      <Modal isOpen={true} toggle={() => this.props.onClose()} autoFocus={false}>
        <ModalHeader toggle={() => this.props.onClose()}>Edit payment type</ModalHeader>
        <ModalBody>
          {body}
          <div className='d-flex column mt-5 center'>
            <BlazeButton
              buttonProps={{
                onClick: this.submit,
                type: 'primary',
                className: 'full-width',
              }}
              titleProps={{
                title: 'Submit',
              }}
            />
          </div>
          <FormError message={this.state.errorMessage} />
        </ModalBody>
      </Modal>
    )
  }
}
